import React from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { LockOpen } from 'lucide-react';
import { format } from 'date-fns';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { parseApiDate } from 'utils/date';

interface SealOpenedDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Created_at of the scan that opened the seal. Null/undefined omits the date clause. */
  tagOpenedAt?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
}

/**
 * VERA-628: "Opened" bottom sheet, opened by tapping the certificate's Opened badge.
 * Renamed from ContactSupportDrawer as part of the product reframe: a cut NTAG 424
 * tamper loop means the seal was opened, which is the expected, common case on a
 * Closure tag (the owner opening their own item) — not an alarm. Content-height
 * DrawerPopup (VERA-842 default) — no `paperSx={{ height: '100vh' }}` override.
 */
export const SealOpenedDrawer: React.FC<SealOpenedDrawerProps> = ({
  open,
  onClose,
  tagOpenedAt,
  supportEmail,
  supportPhone,
}) => {
  // `parseApiDate`, not `new Date`: `tag_opened_at` is selected in raw SQL and arrives as
  // `2026-07-20 05:39:29.441537`, which WebKit refuses to parse. An unguarded
  // `format(new Date(...))` throws RangeError on iOS and takes this sheet down. Returning
  // null instead falls through to the already-written no-date copy.
  const openedAt = parseApiDate(tagOpenedAt);
  const formattedDate = openedAt ? format(openedAt, 'd MMMM yyyy') : null;

  const bodyCopy = formattedDate
    ? `The seal on this item's tag was opened on ${formattedDate}. If you opened it yourself, no action is needed. If you received it sealed, contact the brand.`
    : "The seal on this item's tag has been opened. If you opened it yourself, no action is needed. If you received it sealed, contact the brand.";

  const hasContact = Boolean(supportEmail || supportPhone);

  return (
    <DrawerPopup
      title='Opened'
      /*
        VRG-720 — the SAME glyph the badge that opened this sheet carries
        (`TagOpenedBadge` → lucide `LockOpen`), so the sheet reads as that badge
        expanded rather than as an unrelated surface that happens to share a word.
        `headerLeft` is the slot DrawerPopup already provides for exactly this: it
        travels inside the title Stack, so the icon sits beside "Opened" rather than
        being pushed to the far edge by the header's space-between.

        `aria-hidden` — the title already says "Opened", and a screen reader
        announcing a lock glyph before it would say the state twice.
      */
      headerLeft={<LockOpen size={20} strokeWidth={1.25} aria-hidden />}
      rightActionLabel='Cancel'
      rightActionOnClick={onClose}
      handleOpen={open}
      handleClose={onClose}
      child={
        <Stack spacing={2}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'text.secondary',
            }}
          >
            {bodyCopy}
          </Typography>

          {hasContact && <Divider />}

          {supportEmail && (
            <div className='flex flex-col items-start gap-0'>
              <Typography fontSize={14} fontWeight={400} lineHeight='24px' color='text.secondary'>
                Email
              </Typography>
              <a href={`mailto:${supportEmail}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                <Typography fontSize={16} fontWeight={400} lineHeight='24px'>
                  {supportEmail}
                </Typography>
              </a>
            </div>
          )}

          {supportPhone && (
            <div className='flex flex-col items-start gap-0'>
              <Typography fontSize={14} fontWeight={400} lineHeight='24px' color='text.secondary'>
                Contact phone
              </Typography>
              <a href={`tel:${supportPhone}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                <Typography fontSize={16} fontWeight={400} lineHeight='24px'>
                  {supportPhone}
                </Typography>
              </a>
            </div>
          )}
        </Stack>
      }
    />
  );
};

export default SealOpenedDrawer;
